// src/features/users/components/UserEdit.tsx

import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  required,
  useRecordContext,
  useDataProvider,
  useGetOne,
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Card,
} from '@mui/material';
import { 
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

// 🎯 COMPONENTES DEL SISTEMA
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';
import ChangePasswordDialog from '../../../components/dialogs/ChangePasswordDialog';
// ✨ USANDO TU SISTEMA DE LAYOUT DE FORMA IDIOMÁTICA
import { 
  TwoColumnLayout, 
  MyFormSection 
} from '../../../components/forms/MyFormLayout';

// 🔧 HOOKS Y TIPOS ORIGINALES
import { useUserValidation } from '../hooks/useUserValidation';
import { User } from '../types';


// Componente UserEditTitle
const UserEditTitle = () => {
    const record = useRecordContext<User>();
    return <span>Editar Usuario: {record ? record.name : ''}</span>;
};


// Componente HmiTerminalAccess sin cambios
const HmiTerminalAccess = () => {
    const record = useRecordContext<User>();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [newPin, setNewPin] = useState('');

    if (!record || !record.staffUserIdCode) return null; 

    const handleResetPin = async () => {
        if (newPin.length !== 4) {
            notify('El PIN debe tener 4 dígitos', { type: 'warning' });
            return;
        }
        try {
            await (dataProvider as any).resetUserPin(record.id, newPin);
            notify('PIN reiniciado con éxito', { type: 'success' });
            setPinDialogOpen(false);
            window.location.reload(); 
        } catch (error: any) {
            notify(`Error al reiniciar PIN: ${error.message}`, { type: 'error' });
        }
    };
    
    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={`Staff ID: ${record.staffUserIdCode}`} />
                <Chip
                    label={record.hasPinConfigured ? 'PIN Configurado' : 'PIN No Configurado'}
                    color={record.hasPinConfigured ? 'success' : 'default'}
                />
            </Box>
            <Typography variant="body2" color="text.secondary">
                Formato de login: {record.staffUserIdCode}XXXX (Staff ID + PIN)
            </Typography>
            <Button 
                variant="outlined" 
                onClick={() => setPinDialogOpen(true)}
                sx={{ alignSelf: 'flex-start', mt: 1 }}
            >
                Resetear PIN
            </Button>
        </Box>
        <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)}>
            <DialogTitle>Resetear PIN para {record.name}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus margin="dense" label="Nuevo PIN de 4 dígitos" value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                    inputProps={{ maxLength: 4, pattern: '[0-9]*' }}  variant="standard"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPinDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleResetPin} variant="contained">Guardar PIN</Button>
            </DialogActions>
        </Dialog>
      </>
    );
};

/**
 * 🎨 USER EDIT - Componente principal
 * IMPORTANTE: Obtenemos el ID del usuario desde la URL
 */
export const UserEdit: React.FC = () => {
    const redirect = useRedirect();
    const { id } = useParams<{ id: string }>(); // Obtener ID de la URL
    const { validateName } = useUserValidation();
    
    // Estados para los diálogos
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    
    // Obtener los datos del usuario usando el ID de la URL
    const { data: record } = useGetOne<User>('users', { id });
    
    const handleCancel = () => redirect('/users');
    const handleDeleteClick = () => setShowDeleteDialog(true);
    const handleDeleteClose = () => setShowDeleteDialog(false);
    
    const handleDeleteSuccess = () => {
        setShowDeleteDialog(false);
       
        redirect('/users'); // Redirigir al listado después de eliminar
    };

    const deleteMessage = record 
        ? `¿Estás seguro de que quieres eliminar a ${record.name}?`
        : '¿Estás seguro de que quieres eliminar este usuario?';
    
  
    
    return (
        <Edit title={<UserEditTitle />} mutationMode="pessimistic" sx={{ p: 0 }}>
            <MyFormContainer>
                <SimpleForm
                    toolbar={
                        <MyToolbar 
                            onCancel={handleCancel} 
                            resource="users" 
                            showDelete 
                            onDelete={handleDeleteClick}
                        />
                    }
                    sx={{ minHeight: 'calc(100vh - 300px)' }}
                >
                    <TwoColumnLayout >
                    
                            <MyFormSection
                                title="Información del Usuario"
                                subtitle="Datos básicos. El email y el rol no son editables."
                                icon={<PersonIcon />}
                                variant='outlined'
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 3 }}>
                                    <TextInput 
                                        source="name" 
                                        label="Nombre Completo" 
                                        validate={[required(), validateName]} 
                                    />
                                    <TextInput source="email" label="Email" disabled />
                                    <TextInput source="role" label="Rol" disabled />
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => {
                                            setShowPasswordDialog(true);
                                        }}
                                        sx={{ maxWidth:'200px', alignSelf: 'center' }}
                                        disabled={!record && !id} // Desabilitar si no hay datos
                                    >
                                        Cambiar Contraseña
                                    </Button>
                                </Box>
                            </MyFormSection>
                        
                                <MyFormSection
                                    title="Asignación Actual"
                                    subtitle="Hotel o grupo al que pertenece el usuario."
                                    icon={<BusinessIcon />}
                                    variant='outlined'
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignContent: 'center', gap: 3, px: 3 }}>
                                        <TextInput source="hotel.name" label="Hotel Asignado" disabled />
                                        <TextInput source="group.name" label="Grupo Asignado" disabled />
                                    </Box>
                               

                                {(record && record.staffUserIdCode) && (
                                    <Card sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, m: 1, pb:1 }}>
                                        <Typography variant="h6" sx={{ px: 3, pt: 1, pb:1 }}>Acceso a HMI</Typography>
                                        
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignContent: 'center', gap: 3, px: 3 }}>
                                            <HmiTerminalAccess />
                                        </Box>
                                    </Card>
                                )}
                            </MyFormSection>
                            </TwoColumnLayout>
                </SimpleForm>
            </MyFormContainer>
        
            {showDeleteDialog && (record || id) && (
                <BaseDeleteDialog
                    open={showDeleteDialog}
                    onClose={handleDeleteClose}
                    onConfirm={handleDeleteSuccess}
                    resource="users"
                    recordId={record?.id || id!}
                    title="Eliminar Usuario"
                    message={deleteMessage}
                    warningMessage="Esta acción no se puede deshacer."
                />
            )}

            <ChangePasswordDialog
                open={showPasswordDialog}
                onClose={() => setShowPasswordDialog(false)}
                userId={record?.id || id} // Usar el ID del record o el de la URL
                userName={record?.name}
                isCurrentUser={false}
            />
        </Edit>
    );
};

export default UserEdit;